import { Grid3x3, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';
import { heatColor, heatStyle } from './capabilityAnalyticsUtils';

function Heatmap({ heatmap, onDrill, cohortFilter = 'all' }) {
  const { cohorts = [], rows = [] } = heatmap || {};
  if (!rows.length) {
    return <div className="py-16 text-center text-sm text-slate-400">No roles to display.</div>;
  }
  // When a single cohort is picked, narrow the columns (and each row's cells)
  // to just that cohort so drill-downs land tighter.
  const filtered = cohortFilter !== 'all';
  const visibleCohorts = filtered ? cohorts.filter(c => c.cohort_id === cohortFilter) : cohorts;
  return (
    <div className="overflow-x-auto" data-testid="cap-heatmap">
      <table className="w-full border-separate" style={{ borderSpacing: '3px' }}>
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-white text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide px-2 py-1 min-w-[180px]">
              Job Role
            </th>
            <th className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide px-1 py-1 text-center min-w-[64px]">All</th>
            {visibleCohorts.map(c => (
              <th key={c.cohort_id} className="text-[10px] font-medium text-slate-500 px-1 py-1 text-center min-w-[64px]">
                <span className="block truncate max-w-[80px] mx-auto" title={c.cohort_name}>{c.cohort_name}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const cells = filtered ? r.cells.filter(cell => cell.cohort_id === cohortFilter) : r.cells;
            return (
            <tr key={r.role_id} data-testid={`cap-heatmap-row-${r.role_id}`}>
              <td className="sticky left-0 z-10 bg-white px-2 py-1">
                <button onClick={() => onDrill(r.role_id, filtered ? cohortFilter : null)}
                  className="text-left text-sm font-medium text-slate-700 hover:text-cyan-700 truncate block max-w-[170px]"
                  title={r.role_name} data-testid={`cap-heatmap-role-${r.role_id}`}>
                  {r.role_name}
                </button>
                <span className="text-[10px] text-slate-400">{r.ready_count}/{r.assigned_count} ready</span>
              </td>
              <td className="text-center">
                <button onClick={() => onDrill(r.role_id, null)}
                  style={heatStyle(r.overall_coverage_pct)}
                  className={cn('w-full h-11 rounded-md text-xs font-bold tabular-nums flex items-center justify-center ring-1 ring-inset ring-black/5 transition-transform hover:scale-[1.04]', heatColor(r.overall_coverage_pct))}>
                  {r.overall_coverage_pct}%
                </button>
              </td>
              {cells.map((cell, i) => (
                <td key={i} className="text-center">
                  <button
                    onClick={() => cell.coverage_pct != null && onDrill(r.role_id, cell.cohort_id)}
                    disabled={cell.coverage_pct == null}
                    title={cell.coverage_pct == null ? 'No holders in this cohort' : `${cell.coverage_pct}% · ${cell.ready}/${cell.holders} ready`}
                    style={heatStyle(cell.coverage_pct)}
                    className={cn('w-full h-11 rounded-md text-[11px] font-semibold tabular-nums flex flex-col items-center justify-center ring-1 ring-inset ring-black/5 transition-transform',
                      cell.coverage_pct != null ? 'hover:scale-[1.04] cursor-pointer' : 'cursor-default', heatColor(cell.coverage_pct))}
                    data-testid={`cap-heatmap-cell-${r.role_id}-${cell.cohort_id}`}>
                    {cell.coverage_pct == null ? '—' : `${cell.coverage_pct}%`}
                    {cell.coverage_pct != null && <span className="text-[9px] font-normal opacity-80">{cell.holders}p</span>}
                  </button>
                </td>
              ))}
            </tr>
            );
          })}
        </tbody>
      </table>
      <div className="flex items-center gap-3 mt-4 flex-wrap text-[11px] text-slate-500">
        <span className="font-medium">Coverage:</span>
        {[['<35%', '#f43f5e'], ['35–55%', '#fb923c'], ['55–75%', '#fcd34d'], ['75–90%', '#6ee7b7'], ['≥90%', '#10b981']].map(([l, c]) => (
          <span key={l} className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded" style={{ backgroundColor: c }} /> {l}</span>
        ))}
        <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-slate-100 ring-1 ring-inset ring-slate-200" /> no holders</span>
      </div>
    </div>
  );
}

export function HeatmapCard({ heatmap, onDrill, cohortFilter = 'all', onCohortFilterChange }) {
  const cohorts = heatmap?.cohorts || [];
  return (
    <Card className="border border-slate-200 shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <Grid3x3 className="h-4 w-4 text-cyan-600" /> Role × Cohort coverage heatmap
            </CardTitle>
            <p className="text-[11px] text-slate-500">Current avg coverage per role, split by cohort. Click a cell to drill into that role &amp; cohort.</p>
          </div>
          {cohorts.length > 0 && onCohortFilterChange && (
            <Select value={cohortFilter} onValueChange={onCohortFilterChange}>
              <SelectTrigger className="h-8 w-[190px] bg-white border-slate-200 text-xs" data-testid="cap-heatmap-cohort-filter">
                <Users className="h-3.5 w-3.5 mr-1 text-slate-400" />
                <SelectValue placeholder="All cohorts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All cohorts</SelectItem>
                {cohorts.map(c => <SelectItem key={c.cohort_id} value={c.cohort_id}>{c.cohort_name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Heatmap heatmap={heatmap} onDrill={onDrill} cohortFilter={cohortFilter} />
      </CardContent>
    </Card>
  );
}
